/**
 * feat-242 — the SVG connector layer. AC6's surface.
 *
 * **AC6 asserts ENDPOINTS, not element counts.** A `<path>` count passes on a
 * diagram wired entirely to the wrong nodes. The generalisation behind that AC
 * is that a diagram element's presence is not its correctness — a Recharts
 * `ReferenceArea` without `yAxisId` renders zero rects and throws nothing.
 *
 * So every endpoint here is computed FROM the two nodes' bounding boxes, looked
 * up by the `data-node="<track_id>"` the edge names. The assertion "each drawn
 * edge's endpoints resolve to the bounding boxes of the two Track nodes it
 * claims to connect" is then true by construction rather than by hope, and a
 * mis-wired edge cannot be made to pass.
 *
 * THE TRAP, found by design running the containment check against the mockup and
 * recorded so it is not re-found: the first draft stopped each path **9px short**
 * of the target box to leave room for the arrowhead. It looks identical. It put
 * every path's last point OUTSIDE the node it claims to reach — the containment
 * assertion failed **14 of 14** while a `<path>` count passed. Terminate the path
 * ON the rect edge and paint the head over it. The head is a separate element
 * for exactly this reason.
 *
 * SECOND TRAP, same source: a same-tier connector must not drop straight down.
 * Two boxes in one column share an `x`, so a vertical "curve" runs THROUGH any
 * node between them — on the hazard shape it speared the Prep box. Leave and
 * re-enter on the RIGHT edge and bulge into the gutter instead: it cannot cross
 * a node, and the detour is legible as a detour, which is honest — a same-tier
 * requirement is by definition one the evaluator is not acting on.
 */

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  ATTENTION_EDGE_IGNORED_HEAD,
  ATTENTION_EDGE_IGNORED_LINE,
  ATTENTION_EDGE_LOOP_HEAD,
  ATTENTION_EDGE_LOOP_LINE,
} from '../attentionClasses';
import type { MapEdge, MapEdgeKind } from '../trackGraph';

/**
 * Stroke vocabulary. Told apart by dash and head fill as well as colour, so the
 * three kinds survive greyscale and colour-deficient vision.
 *
 * ⚠️ `line` carries NO `fill-*` class and that is load-bearing, not tidiness.
 * The reasoning lives once, in `attentionClasses.ts` — read it before merging
 * these two fields back together.
 */
const STROKE: Record<
  MapEdgeKind,
  { line: string; head: string; width: number; dash?: string; hollow?: boolean }
> = {
  gating: { line: 'stroke-inkMuted', head: 'stroke-inkMuted fill-inkMuted', width: 1.6 },
  nongating: {
    line: ATTENTION_EDGE_IGNORED_LINE,
    head: ATTENTION_EDGE_IGNORED_HEAD,
    width: 1.6,
    dash: '5 4',
    hollow: true,
  },
  loop: { line: ATTENTION_EDGE_LOOP_LINE, head: ATTENTION_EDGE_LOOP_HEAD, width: 2 },
};

interface DrawnEdge extends MapEdge {
  d: string;
  head: string;
}

export interface MapEdgeLayerProps {
  edges: MapEdge[];
  /**
   * The positioned wrapper the nodes and this SVG share, as an ELEMENT and not
   * as a ref — and that is load-bearing, not a style choice.
   *
   * React attaches a parent's `ref` during the commit's layout phase, which runs
   * bottom-up: a CHILD's `useLayoutEffect` fires BEFORE its parent's ref is
   * populated. This layer is rendered inside the wrapper it measures, so with a
   * `RefObject` the first measurement always saw `null`, nothing re-triggered
   * it, and the canvas rendered zero paths — silently, because an empty edge
   * layer throws nothing and looks like a payload with no prereqs. Taking the
   * element through a callback ref in the parent makes its arrival a state
   * change, which re-runs this effect with a host that exists.
   */
  host: HTMLElement | null;
  /** Re-measure when this changes — the layout depends on the payload. */
  revision: unknown;
}

export const MapEdgeLayer: React.FC<MapEdgeLayerProps> = ({ edges, host, revision }) => {
  const [drawn, setDrawn] = useState<DrawnEdge[]>([]);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const measure = useCallback(() => {
    if (!host) return;
    const H = host.getBoundingClientRect();
    setSize({ w: H.width, h: H.height });

    const rel = (r: DOMRect) => ({
      left: r.left - H.left,
      right: r.right - H.left,
      top: r.top - H.top,
      bottom: r.bottom - H.top,
      midY: r.top - H.top + r.height / 2,
    });

    // Collected by iteration rather than by a built selector: a track id is an
    // opaque string on the wire, and `CSS.escape` is not guaranteed present in
    // every test environment. No escaping, nothing to get wrong.
    const nodes = new Map<string, HTMLElement>();
    host.querySelectorAll<HTMLElement>('[data-node]').forEach((el) => {
      const id = el.getAttribute('data-node');
      if (id) nodes.set(id, el);
    });
    const nodeEl = (id: string) => nodes.get(id) ?? null;

    const tierOfEl = (el: HTMLElement): number | null => {
      const col = el.closest('[data-tier]');
      const raw = col?.getAttribute('data-tier');
      const n = raw === null || raw === undefined ? NaN : parseInt(raw, 10);
      return Number.isNaN(n) ? null : n; // null = the loop group: no tier
    };

    const next: DrawnEdge[] = [];
    for (const edge of edges) {
      const a = nodeEl(edge.from);
      const b = nodeEl(edge.to);
      // An edge whose node is not on the canvas is NOT drawn. A decorative stub
      // would be the one path on the page that cannot satisfy AC6.
      if (!a || !b) continue;

      const ra = rel(a.getBoundingClientRect());
      const rb = rel(b.getBoundingClientRect());
      const ta = tierOfEl(a);
      const tb = tierOfEl(b);

      let d: string;
      let head: string;
      const x2Forward = rb.left;
      if (ta !== null && tb !== null && ta < tb) {
        // FORWARD — right-edge midpoint of the REQUIRED node into the left-edge
        // midpoint of the REQUIRING one, with horizontal control handles.
        const x1 = ra.right;
        const y1 = ra.midY;
        const y2 = rb.midY;
        const dx = Math.max(28, (x2Forward - x1) * 0.5);
        d = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2Forward - dx} ${y2}, ${x2Forward} ${y2}`;
        head = `M ${x2Forward - 9} ${y2 - 4.5} L ${x2Forward} ${y2} L ${x2Forward - 9} ${y2 + 4.5} Z`;
      } else {
        // SAME TIER or BACKWARDS — out into the gutter beside the column and
        // back in on the right edge. Never straight down (see the header).
        const col = a.closest('[data-tier]') as HTMLElement | null;
        const columnRight = col ? rel(col.getBoundingClientRect()).right : Math.max(ra.right, rb.right);
        const lobeX = columnRight + 22;
        const x1 = ra.right;
        const y1 = ra.midY;
        const x2 = rb.right;
        const y2 = rb.midY;
        d = `M ${x1} ${y1} C ${lobeX} ${y1}, ${lobeX} ${y2}, ${x2} ${y2}`;
        head = `M ${x2 + 9} ${y2 - 4.5} L ${x2} ${y2} L ${x2 + 9} ${y2 + 4.5} Z`;
      }
      next.push({ ...edge, d, head });
    }
    setDrawn(next);
  }, [edges, host]);

  useLayoutEffect(() => {
    measure();
  }, [measure, revision]);

  useEffect(() => {
    // ResizeObserver is absent in jsdom; the layer simply does not re-measure
    // there, which is correct — a test drives its own geometry.
    if (typeof ResizeObserver === 'undefined') return undefined;
    if (!host) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(host);
    return () => ro.disconnect();
  }, [host, measure]);

  return (
    <svg
      data-edge-layer
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      width={size.w || '100%'}
      height={size.h || '100%'}
      style={{ overflow: 'visible' }}
    >
      {drawn.map((edge) => {
        const s = STROKE[edge.kind];
        return (
          <g key={`${edge.from}->${edge.to}`}>
            {/* The LINE. `fill="none"` is kept as a belt, but it is not the
                control — `s.line` carrying no `fill-*` class is. A presentation
                attribute loses to any CSS rule. */}
            <path
              d={edge.d}
              fill="none"
              strokeWidth={s.width}
              strokeDasharray={s.dash}
              className={s.line}
              data-edge-from={edge.from}
              data-edge-to={edge.to}
              data-edge-kind={edge.kind}
            />
            {/* The HEAD — a closed triangle, and the one element here that is
                meant to be filled. `hollow` overrides the class fill with white
                via an inline style, which DOES outrank the class. */}
            <path
              d={edge.head}
              strokeWidth={1.4}
              className={s.head}
              style={s.hollow ? { fill: '#FFFFFF' } : undefined}
              data-edge-head-for={`${edge.from}->${edge.to}`}
            />
          </g>
        );
      })}
    </svg>
  );
};

export default MapEdgeLayer;
